/**
 * Shared enumerations — the single source of truth for domain value types.
 *
 * Each enum is a `const` tuple so we get both a runtime array and a union type
 * (via `typeof X[number]`) without a second definition to keep in sync.
 * Zod schemas in shared/schemas reuse these via `z.enum(ROLE)`.
 */

// ── Identity & access ──────────────────────────────────────────────
export const ROLE = ['Admin', 'Manager', 'Employee'] as const;
export type Role = (typeof ROLE)[number];

// ── Employee ───────────────────────────────────────────────────────
export const EMPLOYMENT_STATUS = ['Active', 'OnLeave', 'Terminated'] as const;
export type EmploymentStatus = (typeof EMPLOYMENT_STATUS)[number];

export const WORK_LOCATION = ['OnSite', 'Remote'] as const;
export type WorkLocation = (typeof WORK_LOCATION)[number];

// ── NAID compliance ────────────────────────────────────────────────
export const I9_STATUS = ['Complete', 'Pending', 'Contractor1099', 'NotApplicable'] as const;
export type I9Status = (typeof I9_STATUS)[number];

export const NAID_COMPLIANCE_STATUS = [
  'Compliant',
  'DueSoon',
  'Overdue',
  'Incomplete',
  'NotApplicable',
] as const;
export type NaidComplianceStatus = (typeof NAID_COMPLIANCE_STATUS)[number];

// ── Company assets ─────────────────────────────────────────────────
export const ASSET_TYPE = ['KeyFob'] as const;
export type AssetType = (typeof ASSET_TYPE)[number];

export const ASSET_STATUS = ['Assigned', 'Returned', 'Lost', 'Deactivated'] as const;
export type AssetStatus = (typeof ASSET_STATUS)[number];

// ── Contacts (employees + vendors) ─────────────────────────────────
export const CONTACT_KIND = ['email', 'phone'] as const;
export type ContactKind = (typeof CONTACT_KIND)[number];

export const CONTACT_LABEL = ['Work', 'Personal', 'Mobile', 'Home', 'Other'] as const;
export type ContactLabel = (typeof CONTACT_LABEL)[number];

// ── Vendors ────────────────────────────────────────────────────────
export const VENDOR_STATUS = ['Active', 'Inactive'] as const;
export type VendorStatus = (typeof VENDOR_STATUS)[number];

export const VENDOR_CATEGORY = [
  'Telecom',
  'Facilities',
  'Software',
  'Hardware',
  'ProfessionalServices',
  'Insurance',
  'Payroll',
  'Other',
] as const;
export type VendorCategory = (typeof VENDOR_CATEGORY)[number];

// ── Reviews ────────────────────────────────────────────────────────
export const REVIEW_CYCLE_TYPE = [
  '30',
  '60',
  '90',
  'Quarterly',
  'Annual',
  'Weekly',
] as const;
export type ReviewCycleType = (typeof REVIEW_CYCLE_TYPE)[number];

export const REVIEW_CYCLE_STATUS = ['Upcoming', 'Open', 'Closed'] as const;
export type ReviewCycleStatus = (typeof REVIEW_CYCLE_STATUS)[number];

export const REVIEW_STATUS = ['Draft', 'Shared'] as const;
export type ReviewStatus = (typeof REVIEW_STATUS)[number];

export const FEEDBACK_RELATIONSHIP = ['Peer', 'Manager', 'Direct', 'Self'] as const;
export type FeedbackRelationship = (typeof FEEDBACK_RELATIONSHIP)[number];

// ── Recognition ────────────────────────────────────────────────────
export const RECOGNITION_BADGE = [
  'Teamwork',
  'Innovation',
  'Leadership',
  'CustomerFocus',
  'AboveAndBeyond',
] as const;
export type RecognitionBadge = (typeof RECOGNITION_BADGE)[number];

// ── Disciplinary ───────────────────────────────────────────────────
export const SEVERITY = ['Verbal', 'Written', 'Final', 'Termination'] as const;
export type Severity = (typeof SEVERITY)[number];

// ── Time off ───────────────────────────────────────────────────────
/** Types available when creating/editing a request. */
export const TIME_OFF_TYPE = ['PTO', 'UTO'] as const;
export type TimeOffType = (typeof TIME_OFF_TYPE)[number];

export const TIME_OFF_DURATION = ['FullDay', 'PartialDay'] as const;
export type TimeOffDuration = (typeof TIME_OFF_DURATION)[number];

/** Includes retired types that may still appear on historical requests. */
export const TIME_OFF_TYPE_STORED = ['PTO', 'Sick', 'UTO', 'Bereavement'] as const;
export type TimeOffTypeStored = (typeof TIME_OFF_TYPE_STORED)[number];

export const TIME_OFF_STATUS = ['Pending', 'Approved', 'Denied'] as const;
export type TimeOffStatus = (typeof TIME_OFF_STATUS)[number];

// ── Onboarding / Offboarding ───────────────────────────────────────
export const ONBOARDING_STATUS = ['InProgress', 'Completed', 'Cancelled'] as const;
export type OnboardingStatus = (typeof ONBOARDING_STATUS)[number];

export const OFFBOARDING_STATUS = ONBOARDING_STATUS;
export type OffboardingStatus = OnboardingStatus;

// ── Documents ──────────────────────────────────────────────────────
export const DOCUMENT_CATEGORY = [
  'Offer',
  'ID',
  'Review',
  'PIP',
  'Other',
  'Avatar',
  'Disciplinary',
  'Conversation',
  'Recognition',
  'TimeOff',
] as const;
export type DocumentCategory = (typeof DOCUMENT_CATEGORY)[number];

export const CONFIDENTIAL_RANK = ['Public', 'Manager', 'Admin'] as const;
export type ConfidentialRank = (typeof CONFIDENTIAL_RANK)[number];

// ── Audit ──────────────────────────────────────────────────────────
export const AUDIT_ACTION = [
  'create',
  'update',
  'delete',
  'share',
  'unshare',
  'approve',
  'deny',
  'acknowledge',
  'complete',
  'cancel',
  'sign_in',
  'sign_out',
] as const;
export type AuditAction = (typeof AUDIT_ACTION)[number];
